import {
  Avatar,
  Badge,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Stack,
} from "@mui/material";
import { IDashboardContract } from "../../../../api/hooks/useContracts";
import {
  ButtonLink,
  CONTRACT_STATUS_COLOR_MAP,
  getFilteredFieldCells,
} from "@synota-io/synota-shared-ui";
import { generatePath } from "react-router-dom";
import { Business, Dashboard, ElectricBolt, FileOpen } from "@mui/icons-material";
import { CONTRACTS_DETAIL_PATH } from "../../../../paths";
import { ContractDetailItem } from "./ContractDetailItem";
import { SearchStateLink } from "../../../../shared/layout/SearchStateLink";
import { IDashboardContractDetails } from "../../../../api/hooks/useContractDetails";
import { ContractDetailsList } from "./ContractDetailsList";
import { useContractFields } from "../hooks/useContractFields";
import { useAuth } from "../../../../api/auth/useAuth";
import { ReactNode, useMemo } from "react";

type DashboardButtonProps = {
  contract: IDashboardContractDetails | IDashboardContract;
  label?: string;
  icon?: ReactNode;
};

const DEFAULT_DASHBOARD_URL_LABEL = "Go to Contract Details";

const ConsumerDashboard = ({
  contract,
  label = DEFAULT_DASHBOARD_URL_LABEL,
  icon = <Dashboard />,
}: DashboardButtonProps) =>
  "consumer_dashboard_url" in contract && contract.consumer_dashboard_url ? (
    <ButtonLink startIcon={icon} target="_blank" to={contract.consumer_dashboard_url + "?link"}>
      {label}
    </ButtonLink>
  ) : null;

const SupplierDashboard = ({
  contract,
  label = DEFAULT_DASHBOARD_URL_LABEL,
  icon = <Dashboard />,
}: DashboardButtonProps) =>
  "supplier_dashboard_url" in contract && contract.supplier_dashboard_url ? (
    <ButtonLink startIcon={icon} target="_blank" to={contract.supplier_dashboard_url + "?link"}>
      {label}
    </ButtonLink>
  ) : null;

export const ContractSummaryCard = ({
  isLoading,
  contract,
  open,
}: {
  contract: IDashboardContract | IDashboardContractDetails;
  open?: boolean;
  isLoading?: boolean;
}) => {
  const { isAdmin } = useAuth();

  const href = contract.id
    ? generatePath(CONTRACTS_DETAIL_PATH, {
        id: contract.shared_uuid,
      })
    : "";

  const cells = getFilteredFieldCells(useContractFields(), contract, "summary");

  const header = useMemo(() => {
    const statusColor = contract.contract_state
      ? CONTRACT_STATUS_COLOR_MAP[contract.contract_state]
      : "primary";

    return (
      <>
        {isAdmin ? (
          <CardHeader
            title={contract.contract_type}
            subheader={contract.contract_state}
            slotProps={{ subheader: { color: statusColor } }}
            action={
              <Stack direction="row" gap={4}>
                <ConsumerDashboard contract={contract} label="Consumer" icon={<ElectricBolt />} />
                <SupplierDashboard contract={contract} label="Supplier" icon={<Business />} />
              </Stack>
            }
            avatar={
              contract.contract_display_name && (
                <Badge
                  badgeContent={contract.unapproved_additional_charges || undefined}
                  color="warning"
                >
                  <Avatar
                    title={contract.contract_state || undefined}
                    sx={(theme) => {
                      const bgcolor = theme.palette[statusColor];
                      return {
                        bgcolor: bgcolor.light,
                        color: bgcolor.dark,
                        width: 38,
                        height: 38,
                      };
                    }}
                  >
                    {contract.contract_display_name
                      .split(" ")
                      .filter((word) => word.length > 2)
                      .slice(0, 2)
                      .map((word) => word.replace(/[^a-z0-9]/gi, "")[0])}
                  </Avatar>
                </Badge>
              )
            }
          />
        ) : null}
        <CardHeader
          action={
            isAdmin ? null : ConsumerDashboard({ contract }) || SupplierDashboard({ contract })
          }
          title={contract.supplier_contract_name || contract.consumer_contract_name}
          subheader={
            contract.supplier_contract_name &&
            contract.supplier_contract_name !== contract.supplier_contract_name
              ? contract.consumer_contract_name
              : undefined
          }
        />
      </>
    );
  }, [contract, isAdmin]);

  return (
    <Card sx={{ display: "flex", flexDirection: "column" }}>
      {open ? (
        header
      ) : (
        <CardActionArea component={SearchStateLink} to={href} sx={{ pr: 4 }}>
          {header}
        </CardActionArea>
      )}
      <Divider />

      {open ? (
        <ContractDetailsList contract={contract} isLoading={isLoading} />
      ) : (
        <>
          <CardContent
            sx={(theme) => ({
              borderRadius: 0,
              bgcolor: theme.palette.augmentColor({
                color: { main: theme.palette.background.paper },
              })[theme.palette.mode],
            })}
          >
            <Stack>
              {cells.map(([def, value]) => {
                return (
                  <ContractDetailItem
                    hideNullable={!isAdmin}
                    key={def.field}
                    title={def.headerName}
                    value={value}
                  />
                );
              })}
            </Stack>
          </CardContent>

          <Divider color="primary" />

          <CardActions sx={{ mt: "auto" }}>
            <Button
              component={SearchStateLink}
              to={href}
              startIcon={<FileOpen />}
              color="neutral"
              fullWidth
            >
              View Details
            </Button>
          </CardActions>
        </>
      )}
    </Card>
  );
};
